import { map } from 'lodash';
import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import currencyFormat from 'mout/number/currencyFormat';
import properCase from 'mout/string/properCase';
import {
  Link,
  Breadcrumbs,
  Typography,
  Card,
  CardContent,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tooltip,
  Chip,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import red from '@material-ui/core/colors/red';
import lightBlue from '@material-ui/core/colors/lightBlue';
import grey from '@material-ui/core/colors/grey';
import CardTitle from 'components/CardTitle';
import CardTitleIcon from 'components/CardTitleIcon';
import Loading from 'components/Loading';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import classNames from 'classnames';
import env from 'config/env';
import ExtendButton from './ExtendButton';
import ReviseButton from './ReviseButton';

const GET_ACCOUNT = gql`
  query GetAccountAndInvoices($accountId: Int!) {
    account(id: $accountId) {
      id
      name
    }
  }
`;

const GET_BILLING_INVOICES = gql`
  query GetBillingInvoices($accountId: Int!) {
    billingInvoices(account_id: $accountId, statuses: ["open", "paid", "draft", "void"]) {
      id
      status
      date
      due_date
      lock_date
      url
      number
      amount
      previous_invoice_number
      lock_extensions {
        id
        notes
        date
        created_by_user {
          id
          first_name
          last_name
        }
      }
    }
  }
`;

const BillingInvoices = ({
  match, // url params
  classes,
}) => {

  const accountId = Number(match.params.account_id);

  const { data } = useQuery(GET_ACCOUNT, {
    variables: {
      accountId,
    },
  });

  const {
    loading: isLoadingInvoices, data: invoicesData, refetch: refetchInvoices,
  } = useQuery(GET_BILLING_INVOICES, {
    variables: {
      accountId,
    },
  });

  const getStripeInvoiceUrl = (invoiceId) => {
    if (env === 'production') {
      return `https://dashboard.stripe.com/invoices/${invoiceId}`;
    }
    return `https://dashboard.stripe.com/test/invoices/${invoiceId}`;
  };

  const handleInvoiceExtended = () => {
    refetchInvoices();
  };

  const handleInvoiceRevised = () => {
    refetchInvoices();
  };

  return (
    <div>

      <Breadcrumbs aria-label="breadcrumb">
        <Link component={RouterLink} to="/accounts">
          Accounts
        </Link>
        {data?.account && (
          <Link component={RouterLink} to={`/account/${accountId}`}>
            {data.account.name}
          </Link>
        )}
        <Typography color="textPrimary">Billing (new)</Typography>
      </Breadcrumbs>

      <br />

      <Card>
        <CardContent>

          <CardTitle>
            <CardTitleIcon>
              <Icon value="plumpy plumpy-vkDKKBC6Xb7o" />
            </CardTitleIcon>
            {'Invoices '}
            {invoicesData?.billingInvoices?.length && `(${invoicesData.billingInvoices.length})`}
          </CardTitle>

          {isLoadingInvoices ? <Loading /> : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Number</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Due date</TableCell>
                    <TableCell>Auto-lock date</TableCell>
                    <TableCell>Public URL</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {map(invoicesData.billingInvoices, (invoice) => {
                    const isOpenOverdue = invoice.status === 'open' && moment(invoice.due_date).isBefore(moment());

                    const extensionLabel = (
                      <>
                        {map(invoice.lock_extensions, (extension) => (
                          <Typography variant="body2" key={extension.id} className={classes.extensionLabelLine}>
                            {extension.created_by_user.first_name}
                            {' '}
                            {extension.created_by_user.last_name.charAt(0)}
                            {' extended to '}
                            {moment(extension.date).format('ll')}
                            {': '}
                            <i>{extension.notes}</i>
                          </Typography>
                        ))}
                      </>
                    );

                    return (
                      <TableRow key={invoice.id}>

                        <TableCell>
                          <Link href={getStripeInvoiceUrl(invoice.id)} target="_blank">
                            {invoice.number}
                          </Link>

                          {invoice.previous_invoice_number && (
                            <div>
                              <Tooltip
                                title={`This invoice was regenerated from previous invoice ${invoice.previous_invoice_number}`}
                                placement="top"
                              >
                                <Typography variant="caption" className={classes.previousInvoiceNumber}>
                                  (From
                                  {' '}
                                  {invoice.previous_invoice_number}
                                  )
                                </Typography>
                              </Tooltip>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            className={classNames({
                              [classes.openInvoice]: invoice.status === 'open' && !isOpenOverdue,
                              [classes.openOverdueInvoice]: isOpenOverdue,
                              [classes.paidInvoice]: invoice.status === 'paid',
                              [classes.voidInvoice]: invoice.status === 'void',
                            })}
                            label={invoice.status && isOpenOverdue ? 'Open (overdue)' : properCase(invoice.status)}
                          />
                        </TableCell>
                        <TableCell>
                          £
                          {currencyFormat(invoice.amount, 2)}
                        </TableCell>
                        <TableCell>{moment(invoice.date).format('ll')}</TableCell>
                        <TableCell>{invoice.due_date ? moment(invoice.due_date).format('ll') : '-'}</TableCell>
                        <TableCell>
                          {invoice.lock_date ? (
                            <div>
                              <span>{moment(invoice.lock_date).format('ll')}</span>
                              <ExtendButton accountId={accountId} invoice={invoice} onExtend={handleInvoiceExtended} />

                              {invoice.lock_extensions.length > 0 && (
                                <Tooltip
                                  title={extensionLabel}
                                  placement="top"
                                >
                                  <Typography variant="body2">(Extended)</Typography>
                                </Tooltip>
                              )}
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {invoice.url ? (
                            <Button href={invoice.url} target="_blank">
                              View
                            </Button>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {invoice.status === 'open' && (
                            <ReviseButton
                              invoiceId={invoice.id}
                              accountId={accountId}
                              onRevise={handleInvoiceRevised}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

        </CardContent>
      </Card>
    </div>
  );
};

const styles = (theme) => ({
  openInvoice: {
    color: lightBlue[500],
    borderColor: lightBlue[500],
  },
  openOverdueInvoice: {
    color: red[500],
    borderColor: red[500],
  },
  voidInvoice: {
    color: grey[500],
    borderColor: grey[500],
  },
  paidInvoice: {
    color: theme.palette.success.main,
    borderColor: theme.palette.success.main,
  },
  extendButton: {
    marginLeft: theme.spacing(1),
    marginTop: -theme.spacing(0.5),
  },
  extensionLabelLine: {
    marginBottom: theme.spacing(1),
  },
});

BillingInvoices.propTypes = {
  match: PropTypes.object.isRequired,
};

export default withStyles(styles)(BillingInvoices);
